import React, {
	type KeyboardEvent,
	type MouseEventHandler,
	type ReactNode,
	useRef,
	useCallback,
	useContext,
	useEffect,
} from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import type { PopupProps } from '@atlaskit/popup';
import { Inline } from '@atlaskit/primitives/compiled';

import {
	FIRST_PARTY_CCP_PRODUCT_IDS,
	useFireCrossFlowAnalyticsEvents,
} from '@atlassian/surface-analytics';
import type {
	CrossFlowInteractedUIActionSubjectIDs,
	CrossFlowViewedUIActionSubjectIDs,
	CrossFlowInteractedUISources,
} from '@atlassian/surface-analytics-specs';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { LOOM_CROSS_SELL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { fg } from '@confluence/feature-gating';

import { useDiscoverabilityPulseActions as useLegacyDiscoverabilityPulseActions } from '../DiscoverabilityPulse/useDiscoverabilityPulse';
import { useDiscoverabilityPulseActions as usePostOfficeDiscoverabilityPulseActions } from '../DiscoverabilityPulse/useDiscoverabilityPulseStatePostOffice';
import { useLoomCrossFlowRequestAccessCallback } from '../useLoomCrossFlowRequestAccessCallback';
import { LoomAdControl } from '../LoomAdControl/LoomAdControl';
import { useLoomAdControlState } from '../LoomAdControl/useLoomAdControlState';

import {
	BaseLoomPopupCard,
	type EntryPointId,
	type TriggerProps as BaseTriggerProps,
} from './BaseLoomPopupCard';

const sourceAdType = 'crossFlow';

const i18n = defineMessages({
	tryNow: {
		id: 'loom-utils.loom-cross-sell-popup-card.tryNow',
		defaultMessage: 'Try now',
		description: 'text on button that launches a cross-sell flow for Loom',
	},
});

const env = process.env.NODE_ENV === 'production' ? 'prod' : 'staging';
const LOOM_CCP_PRODUCT_ID = FIRST_PARTY_CCP_PRODUCT_IDS[env].loom;

const getCrossFlowInteractionSource = (
	entrypointId: EntryPointId,
): CrossFlowInteractedUISources | null => {
	switch (entrypointId) {
		case 'loom-confluence-inline-comment':
			return 'loomConfluenceInlineCommentButton';
		case 'loom-confluence-page-editor-toolbar':
			return 'loomConfluencePageEditorToolbarButton';
		case 'loom-confluence-page-header':
			return 'loomConfluencePageHeaderButton';
		default:
			return null;
	}
};

type TriggerProps = BaseTriggerProps & {
	onClick: MouseEventHandler;
};

type LoomCrossFlowPopupCardProps = {
	description: string;
	// a unique identifier for the entrypoint
	entryPointId: EntryPointId;
	placement?: PopupProps['placement'];
	shouldRenderToParent?: boolean;
	// action subject ID of the entrypoint the popup is triggered from
	sourceActionSubjectId: string;
	title: string;
	trigger: (triggerProps: TriggerProps) => ReactNode;
	sourceComponent: CrossFlowViewedUIActionSubjectIDs;
	sourceContext: CrossFlowInteractedUIActionSubjectIDs;
};

export const LoomCrossFlowPopupCard: React.FC<LoomCrossFlowPopupCardProps> = ({
	description,
	entryPointId,
	placement,
	shouldRenderToParent,
	sourceActionSubjectId,
	title,
	trigger,
	sourceComponent,
	sourceContext,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fireViewedUIEvent, fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();
	const intl = useIntl();
	const ctaRef = useRef<HTMLButtonElement | null>(null);

	const triggerCrossFlow = useLoomCrossFlowRequestAccessCallback({
		sourceComponent,
		sourceContext,
	});
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isInterestedInLoom, notInterestedInLoomCallback } = useLoomAdControlState({
		touchpoint: entryPointId,
	});

	const { stopPulse } = useLegacyDiscoverabilityPulseActions();
	const { stopPostOffice } = usePostOfficeDiscoverabilityPulseActions();

	const stopDiscoverabilityPulse = useCallback(() => {
		if (fg('confluence_loom_x-sell_ep_po_migration')) {
			void stopPostOffice();
			return;
		}

		void stopPulse();
	}, [stopPostOffice, stopPulse]);

	// View event for go/BMD crossflow funnel
	useEffect(() => {
		fireViewedUIEvent(
			{
				action: 'viewed',
				actionSubject: 'button',
				actionSubjectId: sourceComponent,
			},
			{
				source: entryPointId,
				recommendedProductIds: [LOOM_CCP_PRODUCT_ID],
			},
		);
	}, [fireViewedUIEvent, sourceComponent, entryPointId]);

	const onCrossFlowTriggerClick = useCallback(
		async (triggerName: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: triggerName,
					source: triggerName === 'tryNow' ? 'loomCrossFlowPopupCard' : sourceActionSubjectId,
					attributes: {
						adType: sourceAdType,
						entrypoint: entryPointId,
					},
				},
			}).fire();
			// Interaction event for go/BMD crossflow funnel. Only fire if entryPointId is registered in @atlassian/surface-analytics-specs package
			const crossFlowInteractionSource = getCrossFlowInteractionSource(entryPointId);
			crossFlowInteractionSource &&
				fireInteractedUIEvent(
					{
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: sourceContext,
					},
					{
						source: crossFlowInteractionSource,
						recommendedProductIds: [LOOM_CCP_PRODUCT_ID],
						targetProductId: LOOM_CCP_PRODUCT_ID,
					},
				);
			stopDiscoverabilityPulse();
			await triggerCrossFlow();
		},
		[
			createAnalyticsEvent,
			entryPointId,
			triggerCrossFlow,
			sourceActionSubjectId,
			sourceContext,
			fireInteractedUIEvent,
			stopDiscoverabilityPulse,
		],
	);

	const handleNotInterested = useCallback(async () => {
		experienceTracker.abort({
			name: LOOM_CROSS_SELL_EXPERIENCE,
			reason: 'User not interested in Loom',
			attributes: {
				sourceComponent: entryPointId,
				adType: sourceAdType,
			},
		});
		stopDiscoverabilityPulse();
		await notInterestedInLoomCallback();
	}, [entryPointId, experienceTracker, notInterestedInLoomCallback, stopDiscoverabilityPulse]);

	if (!isInterestedInLoom) {
		return null;
	}

	return (
		<BaseLoomPopupCard
			actions={
				<Inline space="space.100">
					<LoomAdControl onDismiss={handleNotInterested} entryPointId={entryPointId} />
					<Button
						appearance="discovery"
						onClick={() => onCrossFlowTriggerClick('tryNow')}
						ref={ctaRef}
					>
						{intl.formatMessage(i18n.tryNow)}
					</Button>
				</Inline>
			}
			description={description}
			entryPointId={entryPointId}
			placement={placement}
			shouldRenderToParent={shouldRenderToParent}
			title={title}
			trigger={(popupTriggerProps) =>
				trigger({
					...popupTriggerProps,
					onClick: () => onCrossFlowTriggerClick(sourceActionSubjectId),
					onKeyDown: (e: KeyboardEvent) => {
						if (e.key === 'Tab') {
							ctaRef.current?.focus();
						}
					},
				})
			}
			sourceAdType={sourceAdType}
		/>
	);
};
